import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import FormikCheckbox from '../../../base/forms/formikCheckbox';
import { StyledFormikField } from '../../../base/forms/input';
import InactiveStep from '../../InactiveStep';
import { useMultiStepFormContext } from '../../context/multistepContext';
import { useTranslationsContext } from '../../context/translationsContext';
import { FormStepProps } from '../../sendReceiptMultistep';
import {
	ButtonWrapper,
	ErrorMessage,
	FormStepHeading,
	FormStepWrapper,
	Label,
	PrevButton,
	PrevButtonMobile,
	ProceedButton,
} from '../../sendReceiptMultistep.styles';
import { CheckBoxWrapper, FieldWrapper, FormWrapper } from './Recipient.styles';
import IUserProfileReducer from '../../../../interfaces/reducers/userProfileReducer';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../../../../reducers';

const RECIPIENT_STEP_INDEX = 1;

export default function Recipient({ values, touched, errors, setFieldValue, setFieldTouched }: FormStepProps) {
	const translations = useTranslationsContext();
	const { currentStepIndex, next, prev, goTo, latestStepIndexVisited } = useMultiStepFormContext();
	const { t } = useTranslation('sendReceiptMultistep');
	const userProfileReducer: IUserProfileReducer = useSelector<IStore, IUserProfileReducer>(
		store => store.userProfileReducer
	);
	const { userProfile, isLoggedIn } = userProfileReducer;

	if (currentStepIndex !== RECIPIENT_STEP_INDEX && latestStepIndexVisited >= RECIPIENT_STEP_INDEX)
		return (
			<InactiveStep
				heading={translations?.inactiveTitles?.recipient || t('inactiveTitles.recipient')}
				onClick={() => goTo(RECIPIENT_STEP_INDEX)}
				touched={touched}
				errors={errors}
			/>
		);

	const isStepComplete =
		values.name && !errors.name && values.email && !errors.email && !errors.phone && !errors.identificationNumber;

	if (currentStepIndex === RECIPIENT_STEP_INDEX) {
		if (isLoggedIn && userProfile && !touched?.name) {
			setFieldValue('identificationNumber', userProfile.identificationNumber);
			touched.name = true;
			setFieldValue('email', userProfile.email || '');
			setFieldValue('name', userProfile.name);
			setFieldValue(
				'phone',
				(userProfile.phoneNumber && userProfile.phoneNumber.startsWith('+354')
					? userProfile.phoneNumber.substring(4)
					: userProfile.phoneNumber) || ''
			);
			setTimeout(() => {
				setFieldTouched('identificationNumber', true, true);
				setFieldTouched('email', true, true);
				setFieldTouched('name', true, true);
				setFieldTouched('phone', true, true);
			});
		}

		return (
			<FormStepWrapper>
				<PrevButtonMobile
					onClick={e => {
						e.stopPropagation();
						prev();
					}}
					>
					{translations?.prevStepMobile || t('prevStepMobile')}
				</PrevButtonMobile>
				<FormStepHeading>
					{translations?.formStepTitles?.recipient || t('formStepTitles.recipient')}
				</FormStepHeading>
				<CheckBoxWrapper>
					<FormikCheckbox
						name="foreignSsn"
						id="foreignSsn"
						label={translations?.recipient?.noIcelandicSocialId || t('recipient.noIcelandicSocialId')}
						setFieldValue={setFieldValue}
					/>
				</CheckBoxWrapper>
				<FormWrapper>
					<FieldWrapper>
						<Label htmlFor="name">{translations?.recipient?.name || t('recipient.name')}*</Label>
						<StyledFormikField
							type="text"
							id="name"
							name="name"
							placeholder={translations?.recipient?.namePlaceholder || t('recipient.namePlaceholder')}
						/>
						{touched.name && errors.name && (
							<ErrorMessage>
								{translations?.recipient?.errors?.[errors.name] || t(`recipient.errors.${errors.name}`)}
							</ErrorMessage>
						)}
					</FieldWrapper>
					{!values.foreignSsn && (
						<FieldWrapper>
							<>
								<Label htmlFor="identificationNumber">
									{translations?.recipient?.socialId || t('recipient.socialId')}*
								</Label>
								<StyledFormikField
									type="text"
									id="identificationNumber"
									name="identificationNumber"
									placeholder={
										translations?.recipient?.socialIdPlaceholder ||
										t('recipient.socialIdPlaceholder')
									}
									maxLength={10}
								/>
								{touched.identificationNumber && errors.identificationNumber && (
									<ErrorMessage>
										{translations?.recipient?.errors?.[errors.identificationNumber] ||
											t(`recipient.errors.${errors.identificationNumber}`)}
									</ErrorMessage>
								)}
							</>
						</FieldWrapper>
					)}
					<FieldWrapper>
						<Label htmlFor="email">{translations?.recipient?.email || t('recipient.email')}*</Label>
						<StyledFormikField
							type="text"
							id="email"
							name="email"
							placeholder={translations?.recipient?.emailPlaceholder || t('recipient.emailPlaceholder')}
						/>
						{touched.email && errors.email && (
							<ErrorMessage>
								{translations?.recipient?.errors?.[errors.email] ||
									t(`recipient.errors.${errors.email}`)}
							</ErrorMessage>
						)}
					</FieldWrapper>
					<FieldWrapper>
						<Label htmlFor="phone">{translations?.recipient?.phone || t('recipient.phone')}</Label>
						<StyledFormikField
							type="phone"
							id="phone"
							name="phone"
							placeholder={translations?.recipient?.phonePlaceholder || t('recipient.phonePlaceholder')}
						/>
						{errors.phone && errors.phone && (
							<ErrorMessage>
								{translations?.recipient?.errors?.[errors.phone] ||
									t(`recipient.errors.${errors.phone}`)}
							</ErrorMessage>
						)}
					</FieldWrapper>
				</FormWrapper>
				<ButtonWrapper>
					<PrevButton
						onClick={e => {
							e.stopPropagation();
							prev();
						}}
						>
						{translations?.prevStep || t('prevStep')}
					</PrevButton>
					<ProceedButton
						type="button"
						onClick={e => {
							e.stopPropagation();
							next();
						}}
						disabled={!isStepComplete}>
						{translations?.nextStep || t('nextStep')}
					</ProceedButton>
				</ButtonWrapper>
			</FormStepWrapper>
		);
	}

	return null;
}
